import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';

interface MarkushImportPanelProps {
  /** Opens `text` (CXSMILES, molfile or RGfile) in Markush mode; resolves with what was skipped,
   *  or null when the user cancelled replacing the current drawing. Rejects with a readable error. */
  readonly onImport: (text: string) => Promise<readonly string[] | null>;
}

type ImportState =
  | { readonly state: 'idle' }
  | { readonly state: 'loading' }
  | { readonly state: 'success'; readonly skipped: readonly string[] }
  | { readonly state: 'error'; readonly message: string };

// #663 (Markush M6) -- import a Markush structure from another tool: paste CXSMILES (one line) or a
// molfile/RGfile (many lines), or open the file. Owner decision: import what fits and list what was
// skipped, so success shows the skipped features for review before searching.
export function MarkushImportPanel({ onImport }: MarkushImportPanelProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [importState, setImportState] = useState<ImportState>({ state: 'idle' });
  const fileRef = useRef<HTMLInputElement | null>(null);

  const run = async (input: string) => {
    setImportState({ state: 'loading' });
    try {
      const skipped = await onImport(input);
      setImportState(skipped === null ? { state: 'idle' } : { state: 'success', skipped });
    } catch (error) {
      setImportState({
        state: 'error',
        message: error instanceof Error ? error.message : t('markushImportPanel.failed')
      });
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void run(text);
  };

  return (
    <section
      className="markush-import-panel"
      aria-labelledby="markush-import-heading"
      data-testid="markush-import-panel"
    >
      <div>
        <h2 id="markush-import-heading">{t('markushImportPanel.heading')}</h2>
        <p>{t('markushImportPanel.subheading')}</p>
      </div>
      <form onSubmit={submit}>
        <label htmlFor="markush-import-input">{t('markushImportPanel.label')}</label>
        <textarea
          id="markush-import-input"
          data-testid="markush-import-input"
          rows={3}
          value={text}
          placeholder={t('markushImportPanel.placeholder')}
          spellCheck="false"
          onChange={(event) => setText(event.target.value)}
        />
        <div className="markush-import-actions">
          <button
            type="button"
            className="icon-button"
            aria-label={t('markushImportPanel.openFile')}
            title={t('markushImportPanel.openFile')}
            onClick={() => fileRef.current?.click()}
          >
            <Icon name="uploadFile" />
          </button>
          <input
            ref={fileRef}
            type="file"
            hidden
            accept=".mol,.rgf,.sdf,.cxsmi,.cxsmiles,.txt"
            data-testid="markush-import-file"
            onChange={async (event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (!file) return;
              const content = await file.text();
              setText(content);
              void run(content);
            }}
          />
          <button
            type="submit"
            className={importState.state === 'loading' ? '' : 'icon-button'}
            aria-label={t('markushImportPanel.importButton')}
            title={t('markushImportPanel.importButton')}
            disabled={importState.state === 'loading' || text.trim() === ''}
          >
            {importState.state === 'loading' ? t('markushImportPanel.importing') : <Icon name="arrowForward" />}
          </button>
        </div>
      </form>
      {importState.state === 'error' ? (
        <p className="import-error" role="alert">
          {importState.message}
        </p>
      ) : null}
      {importState.state === 'success' ? (
        <div className="import-success" role="status" data-testid="markush-import-result">
          <p>{t('markushImportPanel.opened')}</p>
          {importState.skipped.length > 0 ? (
            <>
              <p>{t('markushImportPanel.skipped', { count: importState.skipped.length })}</p>
              <ul>
                {importState.skipped.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
